import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Terminal, Plus, X, Save, Trash2, Eye, EyeOff, ExternalLink, Edit3,
  TrendingUp, Award,
} from "lucide-react";

// Phase 25.2b — Admin: manage public case studies for marketing/sales.
// Public version lives at /case-studies/{slug}.

const EMPTY_METRICS = {
  metric_1: { label: "", value: "", change_pct: "" },
  metric_2: { label: "", value: "", change_pct: "" },
  metric_3: { label: "", value: "", change_pct: "" },
};

export default function AdminCaseStudies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/case-studies");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load case studies");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Phase 25.2c — pick up a "Convert to Case Study" draft when arriving from
  // /app/admin/sales after clicking BUILD DRAFT on a WON prospect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("from_won") === "1") {
      const raw = sessionStorage.getItem("dara_pending_case_study_draft");
      if (raw) {
        try {
          const draft = JSON.parse(raw);
          setEditing(draft);
          toast.success("Draft loaded — review the metrics + quote, then publish.", { duration: 6000 });
        } catch { /* swallow */ }
        sessionStorage.removeItem("dara_pending_case_study_draft");
      }
      // Clean URL
      params.delete("from_won");
      const next = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (next ? `?${next}` : ""));
    }
  }, []);

  const save = async () => {
    if (!editing.customer_name || !editing.headline) {
      toast.error("Customer name + headline required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        customer_name: editing.customer_name,
        customer_logo_url: editing.customer_logo_url || "",
        industry: editing.industry || "",
        headline: editing.headline,
        before_pain: editing.before_pain || "",
        dara_metrics: editing.dara_metrics || {},
        quote_body: editing.quote_body || "",
        quote_attribution: editing.quote_attribution || "",
        outcome: editing.outcome || "",
        cta_text: editing.cta_text || "See how DARA works for your business",
        cta_url: editing.cta_url || "/",
        published: editing.published || false,
      };
      if (editing.slug) {
        await api.patch(`/admin/case-studies/${editing.slug}`, payload);
        toast.success("Case study updated");
      } else {
        const r = await api.post("/admin/case-studies", payload);
        toast.success(`Created · ${r.data.slug}`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this case study? Cannot be undone.")) return;
    try {
      await api.delete(`/admin/case-studies/${slug}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const togglePublish = async (cs) => {
    try {
      await api.patch(`/admin/case-studies/${cs.slug}`, { ...cs, published: !cs.published });
      toast.success(cs.published ? "Unpublished" : "Published live");
      await load();
    } catch (e) { toast.error("Update failed"); }
  };

  const seedTemplate = () => {
    setEditing({
      customer_name: "",
      customer_logo_url: "",
      industry: "Hospitality",
      headline: "How {Customer} cut their close-of-day blind spots from 4 hours to 4 seconds",
      before_pain: "Manual end-of-day reconciliation took 4 hours every night. Owner couldn't tell on a Tuesday morning whether yesterday was good or bad until afternoon. Stock-outs on best-sellers happened weekly.",
      dara_metrics: {
        metric_1: { label: "Close-of-day variance", value: "<1%", change_pct: "-94%" },
        metric_2: { label: "Daily reconciliation time", value: "4 sec", change_pct: "-99%" },
        metric_3: { label: "DARA Briefings actioned", value: "92%", change_pct: "+92%" },
      },
      quote_body: "DARA reads our POS like a CFO would. Within 30 seconds of a Tuesday brunch ending, I know what we made, what's trending, and what to prep for tomorrow.",
      quote_attribution: "Owner-Operator · Hospitality",
      outcome: "Upgraded from spreadsheet-and-vibes to a live operational dashboard. Made 3 menu changes in the first month based on DARA Briefing data. Forecast revenue +12% within 8 weeks.",
      cta_text: "See how DARA works for your café",
      cta_url: "/industries/hospitality",
      published: false,
    });
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-case-studies-page">
      <header className="border-b border-zinc-900 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/app/today" className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-[#FFB000]" />
              <span className="font-heading font-black tracking-tight text-lg">DARA</span>
            </Link>
            <div className="hidden sm:block w-px h-6 bg-zinc-800" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// ADMIN · CASE STUDIES</div>
              <h1 className="font-heading font-black text-lg tracking-tight">Customer Stories</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/case-studies" target="_blank" className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2">
              <ExternalLink className="w-3 h-3" /> VIEW PUBLIC
            </Link>
            <button onClick={seedTemplate} data-testid="cs-template-btn"
              className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300">
              + FROM TEMPLATE
            </button>
            <button onClick={() => setEditing({ published: false, dara_metrics: {} })} data-testid="cs-add-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 inline-flex items-center gap-2">
              <Plus className="w-3.5 h-3.5" /> NEW CASE STUDY
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Industry Demo Videos — Phase 25.2c */}
        <IndustryDemoSection />

        {loading && <div className="text-center py-20 font-mono text-[11px] uppercase tracking-widest text-zinc-600">LOADING…</div>}
        {!loading && items.length === 0 && (
          <div className="border border-zinc-900 bg-zinc-950/40 p-10 text-center">
            <Award className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg">No case studies yet</h3>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto leading-relaxed">
              Click <strong className="text-white">+ FROM TEMPLATE</strong> to start with a hospitality template you can edit,
              or <strong className="text-white">+ NEW CASE STUDY</strong> for a blank one. Once published, they live at
              <code className="text-[#FFB000] mx-1">/case-studies/{`{slug}`}</code> and feed the public index.
            </p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cs-grid">
            {items.map(cs => (
              <CaseStudyCard
                key={cs.slug}
                cs={cs}
                onEdit={() => setEditing(cs)}
                onTogglePublish={() => togglePublish(cs)}
                onDelete={() => remove(cs.slug)}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <CaseStudyEditor
          cs={editing}
          setCs={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
          busy={busy}
        />
      )}
    </div>
  );
}

function CaseStudyCard({ cs, onEdit, onTogglePublish, onDelete }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-4 flex flex-col" data-testid={`cs-card-${cs.slug}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="font-heading font-black text-base text-white truncate flex-1 min-w-0">{cs.customer_name}</div>
        <span className={`flex-shrink-0 px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest ${
          cs.published ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/10" : "border-zinc-700 text-zinc-500"
        }`}>
          {cs.published ? "● LIVE" : "DRAFT"}
        </span>
      </div>
      <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest mb-2">{cs.industry}</div>
      <p className="text-sm text-zinc-300 line-clamp-3 flex-1">{cs.headline}</p>
      <div className="flex items-center gap-1 mt-4 pt-3 border-t border-zinc-900">
        <button onClick={onEdit} data-testid={`cs-edit-${cs.slug}`}
          className="border border-zinc-800 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1 flex-1 justify-center">
          <Edit3 className="w-3 h-3" /> EDIT
        </button>
        <button onClick={onTogglePublish} data-testid={`cs-toggle-${cs.slug}`}
          className={`border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1 flex-1 justify-center ${
            cs.published ? "border-zinc-800 text-zinc-400 hover:text-white" : "border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10"
          }`}>
          {cs.published ? <><EyeOff className="w-3 h-3" /> HIDE</> : <><Eye className="w-3 h-3" /> PUBLISH</>}
        </button>
        <button onClick={onDelete} data-testid={`cs-delete-${cs.slug}`}
          className="border border-[#FF6B47]/30 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-2.5 py-1.5 inline-flex items-center justify-center">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function CaseStudyEditor({ cs, setCs, onSave, onClose, busy }) {
  const setMetric = (key, field, val) => {
    const next = { ...(cs.dara_metrics || {}) };
    next[key] = { ...(next[key] || {}), [field]: val };
    setCs({ ...cs, dara_metrics: next });
  };
  const metrics = cs.dara_metrics || {};
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl bg-black border-l border-zinc-800 overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between sticky top-0 bg-black/95 backdrop-blur z-10">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">// {cs.slug ? "EDIT" : "NEW"} CASE STUDY</div>
            <div className="font-heading font-black text-base mt-0.5">{cs.customer_name || "Untitled"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Input label="CUSTOMER NAME *" value={cs.customer_name} onChange={v => setCs({ ...cs, customer_name: v })} placeholder="e.g. Brewski's Coffee Co." testid="cs-customer-name" />
          <Input label="LOGO URL" value={cs.customer_logo_url} onChange={v => setCs({ ...cs, customer_logo_url: v })} placeholder="https://example.com/logo.png" testid="cs-logo-url" />
          <Input label="INDUSTRY" value={cs.industry} onChange={v => setCs({ ...cs, industry: v })} placeholder="Hospitality · Mining · Shipping" testid="cs-industry" />
          <Textarea label="HEADLINE *" value={cs.headline} onChange={v => setCs({ ...cs, headline: v })} rows={2}
            placeholder="How {Customer} cut their close-of-day blind spots from 4 hrs to 4 sec" testid="cs-headline" />
          <Textarea label="BEFORE — PAIN POINTS" value={cs.before_pain} onChange={v => setCs({ ...cs, before_pain: v })} rows={3}
            placeholder="What was breaking before DARA?" testid="cs-pain" />

          <div className="border border-zinc-800 bg-zinc-950/40 p-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mb-3">DARA METRICS (3 KPIs)</div>
            {["metric_1", "metric_2", "metric_3"].map((k, i) => (
              <div key={k} className="grid grid-cols-3 gap-2 mb-2">
                <Input label={`#${i + 1} LABEL`} value={metrics[k]?.label || ""} onChange={v => setMetric(k, "label", v)} testid={`cs-metric-${k}-label`} />
                <Input label="VALUE" value={metrics[k]?.value || ""} onChange={v => setMetric(k, "value", v)} placeholder="<1% · $12K/mo · 4 sec" testid={`cs-metric-${k}-value`} />
                <Input label="CHANGE" value={metrics[k]?.change_pct || ""} onChange={v => setMetric(k, "change_pct", v)} placeholder="-94% · +12%" testid={`cs-metric-${k}-change`} />
              </div>
            ))}
          </div>

          <Textarea label="QUOTE BODY" value={cs.quote_body} onChange={v => setCs({ ...cs, quote_body: v })} rows={3} testid="cs-quote-body" />
          <Input label="QUOTE ATTRIBUTION" value={cs.quote_attribution} onChange={v => setCs({ ...cs, quote_attribution: v })} placeholder='"Owner-Operator · Hospitality"' testid="cs-quote-attr" />
          <Textarea label="OUTCOME / RESULTS" value={cs.outcome} onChange={v => setCs({ ...cs, outcome: v })} rows={3} testid="cs-outcome" />
          <Input label="CTA TEXT" value={cs.cta_text} onChange={v => setCs({ ...cs, cta_text: v })} testid="cs-cta-text" />
          <Input label="CTA URL" value={cs.cta_url} onChange={v => setCs({ ...cs, cta_url: v })} testid="cs-cta-url" />

          <label className="flex items-center gap-2 cursor-pointer pt-2 border-t border-zinc-900">
            <input type="checkbox" checked={!!cs.published} onChange={e => setCs({ ...cs, published: e.target.checked })}
              className="w-4 h-4 accent-[#00E676]" data-testid="cs-published" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300">PUBLISH NOW (visible at /case-studies/{`{slug}`})</span>
          </label>
        </div>
        <div className="border-t border-zinc-900 p-4 flex justify-end gap-2 sticky bottom-0 bg-black">
          <button onClick={onClose} disabled={busy} className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">CANCEL</button>
          <button onClick={onSave} disabled={busy} data-testid="cs-save-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-50">
            <Save className="w-3 h-3" /> {busy ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]" />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={placeholder} data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000] resize-none" />
    </label>
  );
}

// ── Phase 25.2c — Industry Demo Videos ─────────────────────
const DEMO_INDUSTRIES = [
  { key: "shipping", label: "Shipping" },
  { key: "mining", label: "Mining" },
  { key: "heavy-machinery", label: "Heavy Machinery" },
  { key: "hospitality", label: "Hospitality" },
  { key: "retail", label: "Retail" },
];

function IndustryDemoSection() {
  const [demos, setDemos] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState({});  // {industry: {video_url, headline}}

  useEffect(() => {
    api.get("/admin/industry-demos")
      .then(r => {
        const m = {};
        for (const d of r.data?.items || []) m[d.industry] = d;
        setDemos(m);
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async (industry) => {
    setBusy(true);
    try {
      const url = editing[industry]?.video_url ?? demos[industry]?.video_url ?? "";
      const headline = editing[industry]?.headline ?? demos[industry]?.headline ?? "";
      const r = await api.put("/admin/industry-demos", { industry, video_url: url, headline });
      setDemos(d => ({ ...d, [industry]: r.data.row }));
      setEditing(e => ({ ...e, [industry]: undefined }));
      toast.success(`${industry} demo updated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (loading) return null;
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 mb-6 p-5" data-testid="industry-demos-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// 60-SECOND DEMO VIDEOS</div>
          <h2 className="font-heading font-bold text-base mt-0.5">Per-industry video override</h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Paste a Loom or YouTube URL. It'll embed on <code className="text-[#FFB000]">/industries/{`{slug}`}</code> the next time a visitor lands. Empty disables it.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {DEMO_INDUSTRIES.map(({ key, label }) => {
          const current = demos[key] || {};
          const draft = editing[key] || {};
          const url = draft.video_url ?? current.video_url ?? "";
          const headline = draft.headline ?? current.headline ?? "";
          const dirty = (draft.video_url !== undefined && draft.video_url !== current.video_url) ||
                        (draft.headline !== undefined && draft.headline !== current.headline);
          return (
            <div key={key} className="border border-zinc-800 bg-black p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid={`demo-row-${key}`}>
              <div className="md:col-span-2 flex items-center gap-2">
                {url
                  ? <span className="px-2 py-0.5 border border-[#00E676]/40 text-[#00E676] bg-[#00E676]/10 font-mono text-[9px] uppercase tracking-widest">● LIVE</span>
                  : <span className="px-2 py-0.5 border border-zinc-700 text-zinc-500 font-mono text-[9px] uppercase tracking-widest">EMPTY</span>}
                <span className="font-mono text-[10px] uppercase tracking-widest text-white">{label}</span>
              </div>
              <div className="md:col-span-5">
                <input
                  type="text"
                  value={url}
                  onChange={e => setEditing(s => ({ ...s, [key]: { ...(s[key] || {}), video_url: e.target.value } }))}
                  placeholder="https://www.loom.com/share/... or https://youtu.be/..."
                  data-testid={`demo-url-${key}`}
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                />
              </div>
              <div className="md:col-span-3">
                <input
                  type="text"
                  value={headline}
                  onChange={e => setEditing(s => ({ ...s, [key]: { ...(s[key] || {}), headline: e.target.value } }))}
                  placeholder="Optional headline override"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                />
              </div>
              <div className="md:col-span-2">
                <button onClick={() => save(key)} disabled={busy || !dirty} data-testid={`demo-save-${key}`}
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed">
                  {dirty ? "SAVE" : "—"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
